import { useAuthStore } from '../store/authStore'

// Maps permission module names to company module keys
const MODULE_MAP: Record<string, string> = {
  pwa_kpi_hub: 'kpi',
  pwa_kpi_submission: 'kpi',
  admin_indicators: 'kpi',
  admin_reports: 'kpi',
  admin_reminders: 'kpi',
  pwa_attendance: 'attendance',
  pwa_timelog: 'timelog',
  pwa_leave: 'leave',
  pwa_project_monitoring: 'project_monitoring',
  pwa_learning: 'learning',
  admin_project_monitoring: 'project_monitoring',
}

export function usePermissions() {
  const user = useAuthStore((state) => state.user)

  const isSuperadmin = user?.role === 'superadmin'

  function hasModule(module: string): boolean {
    if (isSuperadmin) return true
    const modules = user?.company?.enabled_modules
    if (!modules) return true // backward compat: all enabled by default
    return modules[module] !== false
  }

  function can(module: string, action: string = 'view'): boolean {
    if (isSuperadmin) return true
    // Check company module first
    const companyModule = MODULE_MAP[module]
    if (companyModule && !hasModule(companyModule)) return false
    if (!user?.permissions) return false // safe default: deny if permissions not loaded
    const modulePerms = user.permissions[module]
    if (!modulePerms) return false
    return !!modulePerms[action]
  }

  function canAccessPage(module: string): boolean {
    return can(module, 'view')
  }

  function canAny(checks: Array<{ module: string; action?: string }>): boolean {
    return checks.some((c) => can(c.module, c.action || 'view'))
  }

  function getScope(module: string): string {
    if (isSuperadmin) return 'all'
    if (!user?.data_scope) return 'self'
    return user.data_scope[module] || user.data_scope['default'] || 'self'
  }

  return { can, canAccessPage, canAny, getScope, isSuperadmin, hasModule }
}
